import React, { KeyboardEvent, ReactNode, useId, useRef } from 'react';

export interface ScreenTab {
  id: string;
  label: ReactNode;
  content: ReactNode;
  /** Optional short text/number shown after the label (e.g. a count). */
  badge?: ReactNode;
}

export interface ScreenTabsProps {
  tabs: ScreenTab[];
  value: string;
  onChange: (id: string) => void;
  /** Accessible name of the tab list. */
  label: string;
  className?: string;
}

/**
 * Segmented tabs whose panel fills the remaining height of a bounded screen.
 * Inactive panels stay mounted (`hidden`) so in-progress edits survive a tab
 * switch. Arrow keys / Home / End move between tabs (WAI-ARIA tabs pattern).
 */
export const ScreenTabs = ({
  tabs,
  value,
  onChange,
  label,
  className,
}: ScreenTabsProps) => {
  const uid = useId().replace(/:/g, '');
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = Math.max(
    0,
    tabs.findIndex(t => t.id === value)
  );

  const focusTab = (index: number) => {
    const i = (index + tabs.length) % tabs.length;
    onChange(tabs[i].id);
    refs.current[i]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') focusTab(activeIndex + 1);
    else if (e.key === 'ArrowLeft') focusTab(activeIndex - 1);
    else if (e.key === 'Home') focusTab(0);
    else if (e.key === 'End') focusTab(tabs.length - 1);
    else return;
    e.preventDefault();
  };

  return (
    <div className={`bh-tabs ${className || ''}`}>
      <div
        role="tablist"
        aria-label={label}
        className="bh-tabs__list"
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab, i) => {
          const selected = i === activeIndex;
          return (
            <button
              key={tab.id}
              ref={el => {
                refs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${uid}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${uid}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className="bh-tabs__tab"
              onClick={() => onChange(tab.id)}
            >
              <span>{tab.label}</span>
              {tab.badge !== undefined && tab.badge !== null && (
                <span className="bh-tabs__badge">{tab.badge}</span>
              )}
            </button>
          );
        })}
      </div>
      {tabs.map((tab, i) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`${uid}-panel-${tab.id}`}
          aria-labelledby={`${uid}-tab-${tab.id}`}
          hidden={i !== activeIndex}
          tabIndex={0}
          className="bh-tabs__panel"
        >
          {tab.content}
        </div>
      ))}
    </div>
  );
};
